import React from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Typography, Button} from '@mui/material';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormCheckbox from '../shared/FormCheckbox';
import FormTextField from '../shared/FormTextField';

export interface CancelMembershipReasonFormData {
  reasons: {
    customerServiceIssues: boolean;
    issueWearingCore: boolean;
    notProvidingExpectedFeatures: boolean;
    notTrustingTheData: boolean;
    appNotUserFriendly: boolean;
    experiencingConectivityHardwareIssues: boolean;
    notHelpingMeMeetMyGoals: boolean;
    notProvidingValuableInsights: boolean;
    tooExpensive: boolean;
    other: boolean;
  };
  otherReason: string;
  canYouTellUsMore: string;
  ifYoureOpenToSharingWhatsLeadingToTheBreak: string;
  ifYoureWillingToShareWhatsSpecificallyDisappointing: string;
}

export const getCancelMembershipReasons = (
  data: CancelMembershipReasonFormData,
) => {
  const reasons = Object.entries(data.reasons)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value)
    .map(([key]) => key);
  const additionalNotesObject = {
    otherReason: data.otherReason,
    canYouTellUsMore: data.canYouTellUsMore,
    ifYoureOpenToSharingWhatsLeadingToTheBreak:
      data.ifYoureOpenToSharingWhatsLeadingToTheBreak,
    ifYoureWillingToShareWhatsSpecificallyDisappointing:
      data.ifYoureWillingToShareWhatsSpecificallyDisappointing,
  };
  // filter out empty strings
  Object.keys(additionalNotesObject).forEach(key => {
    if (
      additionalNotesObject[key as keyof typeof additionalNotesObject] === ''
    ) {
      delete additionalNotesObject[key as keyof typeof additionalNotesObject];
    }
  });
  const additionalNotes =
    Object.keys(additionalNotesObject).length > 0
      ? JSON.stringify(additionalNotesObject)
      : undefined;

  return {reasons, additionalNotes};
};

interface Props {
  onSubmit: (data: CancelMembershipReasonFormData) => void;
  loading?: boolean;
  onClose: () => void;
}

const CancelMembershipReasonForm: React.FC<Props> = ({
  onSubmit,
  onClose,
  loading = false,
}) => {
  const {t} = useTranslation();
  const {control, handleSubmit, watch} =
    useForm<CancelMembershipReasonFormData>({
      defaultValues: {
        reasons: {
          customerServiceIssues: false,
          issueWearingCore: false,
          notProvidingExpectedFeatures: false,
          notTrustingTheData: false,
          appNotUserFriendly: false,
          experiencingConectivityHardwareIssues: false,
          notHelpingMeMeetMyGoals: false,
          notProvidingValuableInsights: false,
          tooExpensive: false,
          other: false,
        },
        otherReason: '',
        canYouTellUsMore: '',
        ifYoureOpenToSharingWhatsLeadingToTheBreak: '',
        ifYoureWillingToShareWhatsSpecificallyDisappointing: '',
      },
    });

  const reasons = watch('reasons');
  const otherReason = watch('otherReason');
  const showOtherReason = reasons?.other;

  const handleFormSubmit = (data: CancelMembershipReasonFormData) => {
    // Validate that at least one reason is selected
    const hasReasons = Object.values(data.reasons).some(reason => reason);
    if (!hasReasons) {
      return;
    }

    // Validate that other reason is provided if "other" is selected
    if (data.reasons.other && !data.otherReason.trim()) {
      return;
    }

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Typography variant="h5" sx={{mb: 2}}>
        {t('membership.cancelMembershipReasonTitle')}
      </Typography>
      <Typography variant="body2" sx={{mb: 3, color: 'text.secondary'}}>
        {t('membership.cancelMembershipReasonSubtitle')}
      </Typography>

      <Box sx={{mb: 3}}>
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.customerServiceIssues"
          label={t('membership.cancelMembershipReasonCustomerServiceIssues')}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.issueWearingCore"
          label={t('membership.cancelMembershipReasonIssueWearingCore')}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.notProvidingExpectedFeatures"
          label={t(
            'membership.cancelMembershipReasonNotProvidingExpectedFeatures',
          )}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.notTrustingTheData"
          label={t('membership.cancelMembershipReasonNotTrustingTheData')}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.appNotUserFriendly"
          label={t('membership.cancelMembershipReasonAppNotUserFriendly')}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.experiencingConectivityHardwareIssues"
          label={t(
            'membership.cancelMembershipReasonExperiencingConectivityHardwareIssues',
          )}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.notHelpingMeMeetMyGoals"
          label={t('membership.cancelMembershipReasonNotHelpingMeMeetMyGoals')}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.notProvidingValuableInsights"
          label={t(
            'membership.cancelMembershipReasonNotProvidingValuableInsights',
          )}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.tooExpensive"
          label={t('membership.cancelMembershipReasonTooExpensive')}
        />
        <FormCheckbox
          control={control as unknown as Control<FieldValues>}
          name="reasons.other"
          label={t('membership.cancelMembershipReasonOther')}
        />
      </Box>

      {showOtherReason && (
        <Box sx={{mb: 3}}>
          <FormTextField
            control={control as unknown as Control<FieldValues>}
            name="otherReason"
            label={t('membership.cancelReasonOtherDetails') as string}
            required
          />
        </Box>
      )}

      <Box sx={{mb: 2}}>
        <FormTextField
          control={control as unknown as Control<FieldValues>}
          name="canYouTellUsMore"
          label={t('membership.canYouTellUsMore') as string}
        />
        <FormTextField
          control={control as unknown as Control<FieldValues>}
          name="ifYoureOpenToSharingWhatsLeadingToTheBreak"
          label={
            t('membership.ifYoureOpenToSharingWhatsLeadingToTheBreak') as string
          }
        />
        <FormTextField
          control={control as unknown as Control<FieldValues>}
          name="ifYoureWillingToShareWhatsSpecificallyDisappointing"
          label={
            t(
              'membership.ifYoureWillingToShareWhatsSpecificallyDisappointing',
            ) as string
          }
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}>
        <Button
          variant="outlined"
          sx={{mr: 1}}
          type="submit"
          disabled={
            loading ||
            !Object.values(reasons).some(reason => reason) ||
            (reasons.other && !otherReason?.trim())
          }>
          {t('membership.cancelMembership')}
        </Button>
        <Button variant="contained" onClick={onClose} disabled={loading}>
          {t('general.close')}
        </Button>
      </Box>
    </form>
  );
};

export default CancelMembershipReasonForm;
